import React, { useState } from "react";
import type { UseQueryResult } from "@tanstack/react-query";
import type { StoreApi } from "zustand";
import { createStore, useStore } from "zustand";
import { immer } from "zustand/middleware/immer";
import { createSafeContext } from "~/contexts";
import type { LogTag, TaggingMode } from "~/domain/logs";
import { checkIsLogTag } from "~/domain/logs";
import { invariant } from "~/lib/invariant";
import type { ListTagsForLogRequest, Log, Tag, Topic } from "~/lqs";
import { useTag } from "~/lqs";
import { assertNever } from "~/utils";

interface TagStore {
  visibleTags: { type: "log" } | { type: "topic"; topicId: Topic["id"] } | null;
  activeTagId: Tag["id"] | null;
  taggingMode: TaggingMode;
  actions: {
    toggleLogTags: (force?: true) => void;
    toggleTopicTags: (topicId: Topic["id"]) => void;
    setActiveTag: (tag: Tag) => void;
    clearActiveTag: () => void;
    changeTaggingMode: (newTaggingMode: TaggingMode) => void;
  };
}

function createTagStore() {
  return createStore<TagStore>()(
    immer((set) => {
      return {
        visibleTags: { type: "log" },
        activeTagId: null,
        taggingMode: "log",
        actions: {
          toggleLogTags(force) {
            set((state) => {
              if (force) {
                state.visibleTags = { type: "log" };
              } else if (state.visibleTags?.type === "log") {
                state.visibleTags = null;
              } else {
                state.visibleTags = { type: "log" };
              }
            });
          },
          toggleTopicTags(topicId) {
            set((state) => {
              if (
                state.visibleTags?.type === "topic" &&
                state.visibleTags.topicId === topicId
              ) {
                state.visibleTags = null;
              } else {
                state.visibleTags = { type: "topic", topicId };
              }
            });
          },
          setActiveTag(tag) {
            set((state) => {
              state.activeTagId = tag.id;
            });
          },
          clearActiveTag() {
            set((state) => {
              state.activeTagId = null;
            });
          },
          changeTaggingMode(newTaggingMode) {
            set((state) => {
              state.taggingMode = newTaggingMode;
            });
          },
        },
      };
    }),
  );
}

const [useTagStoreContext, TagStoreContext] =
  createSafeContext<StoreApi<TagStore>>("TagStore");

// Tag store must be scoped to a given log, meaning there can't be a single
// store for the entire app. Instead, the `<TagStoreProvider />` will create
// a fresh store whenever it mounts. This component should be rendered under
// the `<LogPlaybackSourceProvider />`
export function TagStoreProvider({ children }: { children: React.ReactNode }) {
  const [tagStore] = useState(createTagStore);

  return (
    <TagStoreContext.Provider value={tagStore}>
      {children}
    </TagStoreContext.Provider>
  );
}

export function usePlaybackTagsFilters(): Pick<
  ListTagsForLogRequest,
  "topicId" | "startTimeNull"
> | null {
  const tagStore = useTagStoreContext();

  return useStore(
    tagStore,
    (
      store,
    ): Pick<ListTagsForLogRequest, "topicId" | "startTimeNull"> | null => {
      if (store.visibleTags === null) {
        return null;
      }

      switch (store.visibleTags.type) {
        case "log": {
          return {
            startTimeNull: false,
          };
        }
        case "topic": {
          return {
            topicId: store.visibleTags.topicId,
            startTimeNull: false,
          };
        }
        default: {
          assertNever(store.visibleTags);
        }
      }
    },
  );
}

export function useActiveTag({
  logId,
}: {
  logId: Log["id"] | null;
}): [query: UseQueryResult<LogTag>, tagSelected: boolean] {
  const tagStore = useTagStoreContext();

  const activeTagId = useStore(tagStore, (store) => store.activeTagId);

  const activeTagQuery = useTag(logId, activeTagId, {
    select({ data: tag }) {
      invariant(checkIsLogTag(tag), "Tag is not a log tag");

      return tag;
    },
  });

  return [activeTagQuery, activeTagId != null];
}

export function useTaggingMode(): TaggingMode {
  const tagStore = useTagStoreContext();

  return useStore(tagStore, (store) => store.taggingMode);
}

export function useAreLogTagsShowing(): boolean {
  const tagStore = useTagStoreContext();

  return useStore(tagStore, (store) => store.visibleTags?.type === "log");
}

export function useAreTopicTagsShowing(topicId: Topic["id"]): boolean {
  const tagStore = useTagStoreContext();

  return useStore(
    tagStore,
    (store) =>
      store.visibleTags?.type === "topic" &&
      store.visibleTags.topicId === topicId,
  );
}

export function useShouldShowTags() {
  const tagStore = useTagStoreContext();

  return useStore(tagStore, (store) => store.visibleTags !== null);
}

export function useTagActions() {
  const tagStore = useTagStoreContext();

  return useStore(tagStore, (store) => store.actions);
}
